import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Pagination,
  Row,
  Table,
  Tag,
  Typography,
  Alert,
  Spin,
  notification,
  DatePicker,
  Select,
  Space,
  Statistic,
  Modal,
  Form,
  Popconfirm,
  Dropdown,
  Grid,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  LinkOutlined,
  StopOutlined,
  CopyOutlined,
  PlusOutlined,
  DisconnectOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { states } from '../../assets/utils/config';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import { getFacilityOnboardingStatusMeta } from './facilityUtils';
import './facilities.css';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const LEGACY_FACILITY_ROLES = [1, 3, 4, 5];

const ONBOARDING_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Linked', value: 'linked' },
  { label: 'Dismissed', value: 'dismissed' },
  { label: 'Duplicate', value: 'duplicate' },
];

const statusTag = (status) => {
  const s = (status || 'pending').toLowerCase();
  const color =
    s === 'linked'
      ? 'green'
      : s === 'dismissed'
        ? 'default'
        : s === 'duplicate'
          ? 'purple'
          : 'orange';
  return <Tag color={color}>{s}</Tag>;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateShort = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const orgCell = (record) => {
  if (record?.organization?.org_name) {
    return (
      <div>
        <Text strong>{record.organization.org_name}</Text>
        {record.organization.org_social_no ? (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.organization.org_social_no}
            </Text>
          </div>
        ) : null}
      </div>
    );
  }
  if (record?.policy?.matched_enrollee) {
    return <Tag color="default">No organization</Tag>;
  }
  return <Tag color="default">Not linked</Tag>;
};

const emptySummary = {
  total: 0,
  with_matched_enrollee: 0,
  with_organization: 0,
  unmatched_enrollee: 0,
  without_organization: 0,
  with_onboarded_facility: 0,
  onboarding_pending: 0,
  onboarding_dismissed: 0,
  onboarding_duplicate: 0,
};

const getFacilityRowId = (f) => f?.id ?? f?.facility_id;

function FacilitySuggestions({ embedded = false } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAnyPermissionWithLegacy, canStayOnModule } = usePermissions();
  const screens = Grid.useBreakpoint();
  const isMdUp = Boolean(screens.md);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stateFilter, setStateFilter] = useState(undefined);
  const [hasOrg, setHasOrg] = useState(undefined);
  const [enrolleeMatched, setEnrolleeMatched] = useState(undefined);
  const [onboardedFilter, setOnboardedFilter] = useState(undefined);
  const [onboardingStatusFilter, setOnboardingStatusFilter] = useState(undefined);
  const [summary, setSummary] = useState(emptySummary);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    pages: 0,
  });
  const [detail, setDetail] = useState(null);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkSuggestion, setLinkSuggestion] = useState(null);
  const [linkFacilityId, setLinkFacilityId] = useState(undefined);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [facilitySearchLoading, setFacilitySearchLoading] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const facilitySearchTimer = useRef(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSuggestion, setCreateSuggestion] = useState(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [tierOptions, setTierOptions] = useState([{ label: 'All Plans (no minimum)', value: null, tier_name: 'all plans' }]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [createForm] = Form.useForm();

  // Permission-first: the legacy staff role ids only apply while the session has
  // no RBAC permission map.
  const canAccessPage = useMemo(
    () => canStayOnModule('facilitySuggestions', LEGACY_FACILITY_ROLES),
    [canStayOnModule]
  );

  const canManageOnboarding = useMemo(
    () => hasAnyPermissionWithLegacy(['facilities.manage', 'admin.*'], [1]),
    [hasAnyPermissionWithLegacy]
  );

  const sharedParams = useMemo(() => {
    const from_date =
      dateRange?.[0] && dayjs.isDayjs(dateRange[0]) ? dateRange[0].format('YYYY-MM-DD') : undefined;
    const to_date =
      dateRange?.[1] && dayjs.isDayjs(dateRange[1]) ? dateRange[1].format('YYYY-MM-DD') : undefined;
    return lib.buildFacilitySuggestionSharedParams({
      search: searchDebounced,
      from_date,
      to_date,
      state: stateFilter,
      has_org: hasOrg,
      enrollee_matched: enrolleeMatched,
      onboarded: onboardedFilter,
      onboarding_status: onboardingStatusFilter,
    });
  }, [
    searchDebounced,
    dateRange,
    stateFilter,
    hasOrg,
    enrolleeMatched,
    onboardedFilter,
    onboardingStatusFilter,
  ]);

  const hasActiveFilters = useMemo(() => {
    const hasDates = Boolean(dateRange?.[0] || dateRange?.[1]);
    const hasState = Boolean(stateFilter);
    const hasTri =
      hasOrg !== undefined ||
      enrolleeMatched !== undefined ||
      onboardedFilter !== undefined ||
      onboardingStatusFilter !== undefined;
    return Boolean(searchDebounced || hasDates || hasState || hasTri);
  }, [
    searchDebounced,
    dateRange,
    stateFilter,
    hasOrg,
    enrolleeMatched,
    onboardedFilter,
    onboardingStatusFilter,
  ]);

  useEffect(() => {
    // When embedded as a tab, the parent screen gates access/visibility, so this
    // instance must never redirect the whole page.
    if (embedded) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!canAccessPage) {
      notification.warning({
        message: 'Access denied',
        description:
          'You need facilities.read_all (or admin.*) to view facility suggestions.',
        placement: 'topLeft',
      });
      navigate('/dashboard');
    }
  }, [embedded, user, canAccessPage, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const validateDateRange = useCallback(() => {
    if (dateRange?.[0] && dateRange?.[1] && dayjs.isDayjs(dateRange[0]) && dayjs.isDayjs(dateRange[1])) {
      if (dateRange[0].isAfter(dateRange[1], 'day')) {
        notification.error({
          message: 'Invalid date range',
          description: 'Start date must be on or before end date.',
          placement: 'topRight',
        });
        return false;
      }
    }
    return true;
  }, [dateRange]);

  const load = useCallback(
    async (page, limit, showSearchSpin = false) => {
      if (!validateDateRange()) return;

      const from_date =
        dateRange?.[0] && dayjs.isDayjs(dateRange[0]) ? dateRange[0].format('YYYY-MM-DD') : undefined;
      const to_date =
        dateRange?.[1] && dayjs.isDayjs(dateRange[1]) ? dateRange[1].format('YYYY-MM-DD') : undefined;

      const filterPayload = {
        search: searchDebounced,
        from_date,
        to_date,
        state: stateFilter,
        has_org: hasOrg,
        enrollee_matched: enrolleeMatched,
        onboarded: onboardedFilter,
        onboarding_status: onboardingStatusFilter,
      };

      const listParams = { page, limit, ...filterPayload };

      if (showSearchSpin) setSearchLoading(true);
      else setLoading(true);
      setSummaryLoading(true);
      setError(null);

      const p = page ?? 1;
      const l = limit ?? 20;

      try {
        const [listResp, sumResp] = await Promise.all([
          lib.getFacilitySuggestionsV2(listParams),
          lib.getFacilitySuggestionsSummaryV2(filterPayload),
        ]);

        if (listResp?.status === 'error') {
          const msg = listResp.msg || 'Failed to load facility suggestions';
          setError(msg);
          notification.error({ message: msg, placement: 'topRight' });
          setRows([]);
          setPagination((prev) => ({ ...prev, current: p, pageSize: l, total: 0, pages: 0 }));
        } else {
          const body = listResp?.data;
          const hasError = body && (Number(body.error) === 1 || body.error === true);
          if (body && !hasError && Array.isArray(body.data)) {
            setRows(body.data);
            setPagination((prev) => ({
              ...prev,
              current: body.pagination?.page ?? p,
              pageSize: body.pagination?.limit ?? l,
              total: body.pagination?.total ?? 0,
              pages: body.pagination?.pages ?? 0,
            }));
          } else {
            const msg = body?.message || body?.data?.details || 'Failed to load facility suggestions';
            setError(msg);
            setRows([]);
            setPagination((prev) => ({
              ...prev,
              current: p,
              pageSize: l,
              total: 0,
              pages: 0,
            }));
          }
        }

        if (sumResp?.status === 'error') {
          setSummary(emptySummary);
        } else {
          const sb = sumResp?.data;
          const sErr = sb && (Number(sb.error) === 1 || sb.error === true);
          if (sb && !sErr && sb.data && typeof sb.data === 'object') {
            setSummary({ ...emptySummary, ...sb.data });
          } else {
            setSummary(emptySummary);
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          notification.warning({
            message: status === 401 ? 'Unauthorized' : 'Forbidden',
            description:
              e?.response?.data?.message ||
              'You do not have permission to view facility suggestions.',
            placement: 'topLeft',
          });
          navigate('/dashboard');
          return;
        }
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.data?.details ||
          e?.message ||
          'Failed to load facility suggestions';
        setError(msg);
        notification.error({ message: msg, placement: 'topRight' });
        setRows([]);
        setSummary(emptySummary);
        setPagination((prev) => ({
          ...prev,
          current: p,
          pageSize: l,
          total: 0,
          pages: 0,
        }));
      } finally {
        setLoading(false);
        setSearchLoading(false);
        setSummaryLoading(false);
      }
    },
    [
      navigate,
      validateDateRange,
      dateRange,
      searchDebounced,
      stateFilter,
      hasOrg,
      enrolleeMatched,
      onboardedFilter,
      onboardingStatusFilter,
    ]
  );

  useEffect(() => {
    if (!user || !canAccessPage) return;
    load(1, pagination.pageSize, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchDebounced,
    dateRange,
    stateFilter,
    hasOrg,
    enrolleeMatched,
    onboardedFilter,
    onboardingStatusFilter,
    user,
    canAccessPage,
  ]);

  useEffect(() => {
    if (!user || !canAccessPage) return;
    let mounted = true;
    const loadTiers = async () => {
      setLoadingTiers(true);
      try {
        const resp = await lib.getAdminTiers({ scope: 'active' });
        const body = resp?.data;
        if (body?.error || !Array.isArray(body?.data?.tiers)) return;
        const dynamicTiers = body.data.tiers
          .filter((tier) => Number.isFinite(Number(tier?.tier_id)))
          .map((tier) => ({
            label: `${tier?.tier_name || `Tier ${tier?.tier_id}`} (Rank: ${tier?.tier_rank ?? '--'})`,
            value: Number(tier?.tier_id),
            tier_name: tier?.tier_name || '',
          }));
        if (mounted) {
          setTierOptions([{ label: 'All Plans (no minimum)', value: null, tier_name: 'all plans' }, ...dynamicTiers]);
        }
      } finally {
        if (mounted) setLoadingTiers(false);
      }
    };
    loadTiers();
    return () => {
      mounted = false;
    };
  }, [user, canAccessPage]);

  const refreshAfterMutation = useCallback(() => {
    load(pagination.current, pagination.pageSize, false);
  }, [load, pagination]);

  const handleExport = async () => {
    if (!validateDateRange()) return;
    setExporting(true);
    try {
      await lib.exportFacilitySuggestionsV2(sharedParams);
      notification.success({ message: 'Download started', placement: 'topRight' });
    } catch (e) {
      notification.error({
        message: e?.message || 'Export failed',
        placement: 'topRight',
      });
    } finally {
      setExporting(false);
    }
  };

  const searchFacilities = (q) => {
    if (facilitySearchTimer.current) clearTimeout(facilitySearchTimer.current);
    facilitySearchTimer.current = setTimeout(async () => {
      const term = (q || '').trim();
      if (term.length < 2) {
        setFacilityOptions([]);
        return;
      }
      setFacilitySearchLoading(true);
      try {
        const resp = await lib.getFacilitiesV2({ search: term, limit: 25, page: 1 });
        const body = resp?.data;
        const list = Array.isArray(body?.data) ? body.data : [];
        setFacilityOptions(
          list.map((f) => ({
            value: getFacilityRowId(f),
            label: `${f.name ?? f.facility_name ?? 'Facility'} (${getFacilityRowId(f)})`,
          }))
        );
      } catch {
        setFacilityOptions([]);
      } finally {
        setFacilitySearchLoading(false);
      }
    }, 400);
  };

  const openLinkModal = (record, e) => {
    e?.stopPropagation();
    setLinkSuggestion(record);
    setLinkFacilityId(undefined);
    setFacilityOptions([]);
    setLinkModalOpen(true);
  };

  const submitLink = async () => {
    if (linkFacilityId == null) {
      notification.warning({ message: 'Select a facility', placement: 'topRight' });
      return;
    }
    setLinkSubmitting(true);
    try {
      const resp = await lib.updateFacilitySuggestionOnboarding(linkSuggestion.id, {
        facility_id: linkFacilityId,
      });
      if (resp?.status === 'error') {
        notification.error({ message: resp.msg || 'Failed to link', placement: 'topRight' });
        return;
      }
      if (Number(resp?.error) === 1) {
        notification.error({ message: resp.message || 'Failed to link', placement: 'topRight' });
        return;
      }
      const body = resp?.data;
      if (body && Number(body.error) === 1) {
        notification.error({ message: body.message || 'Failed to link', placement: 'topRight' });
        return;
      }
      notification.success({ message: body?.message || 'Onboarding updated', placement: 'topRight' });
      setLinkModalOpen(false);
      setLinkSuggestion(null);
      refreshAfterMutation();
      if (detail?.id === linkSuggestion?.id && body?.data) setDetail(body.data);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || err?.message || 'Failed to link',
        placement: 'topRight',
      });
    } finally {
      setLinkSubmitting(false);
    }
  };

  const putOnboardingStatus = async (record, payload, successMsg) => {
    try {
      const resp = await lib.updateFacilitySuggestionOnboarding(record.id, payload);
      if (resp?.status === 'error') {
        notification.error({ message: resp.msg || 'Update failed', placement: 'topRight' });
        return;
      }
      if (Number(resp?.error) === 1) {
        notification.error({ message: resp.message || 'Update failed', placement: 'topRight' });
        return;
      }
      const body = resp?.data;
      if (body && Number(body.error) === 1) {
        notification.error({ message: body.message || 'Update failed', placement: 'topRight' });
        return;
      }
      notification.success({ message: successMsg, placement: 'topRight' });
      refreshAfterMutation();
      if (detail?.id === record.id && body?.data) setDetail(body.data);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || err?.message || 'Update failed',
        placement: 'topRight',
      });
    }
  };

  const onboardingCol = (_, record) => {
    const ob = record.onboarding || {};
    const name = ob.facility_name || '—';
    return (
      <div>
        {statusTag(ob.status)}
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {name}
          </Text>
        </div>
      </div>
    );
  };

  const actionsCol = (_, record) => {
    const stop = (e) => e.stopPropagation();

    if (!isMdUp) {
      if (!canManageOnboarding) {
        return (
          <Button type="link" size="small" onClick={(e) => { stop(e); setDetail(record); }}>
            View
          </Button>
        );
      }
      const menuItems = [
        {
          key: 'view',
          label: 'View details',
          onClick: () => setDetail(record),
        },
        {
          key: 'link',
          label: 'Link to facility',
          icon: <LinkOutlined />,
          onClick: () => {
            setLinkSuggestion(record);
            setLinkFacilityId(undefined);
            setFacilityOptions([]);
            setLinkModalOpen(true);
          },
        },
        ...(record.onboarding?.facility_id != null
          ? [
              {
                key: 'unlink',
                label: 'Unlink facility',
                icon: <DisconnectOutlined />,
                onClick: () => {
                  Modal.confirm({
                    title: 'Clear facility link?',
                    content: 'Returns this suggestion to the pending queue.',
                    okText: 'Unlink',
                    onOk: () =>
                      putOnboardingStatus(record, { facility_id: null }, 'Link cleared'),
                  });
                },
              },
            ]
          : []),
        {
          key: 'dismiss',
          label: 'Dismiss',
          danger: true,
          icon: <StopOutlined />,
          onClick: () => {
            Modal.confirm({
              title: 'Mark as dismissed?',
              okText: 'Dismiss',
              onOk: () =>
                putOnboardingStatus(record, { onboarding_status: 'dismissed' }, 'Marked dismissed'),
            });
          },
        },
        {
          key: 'duplicate',
          label: 'Mark duplicate',
          icon: <CopyOutlined />,
          onClick: () => {
            Modal.confirm({
              title: 'Mark as duplicate?',
              onOk: () =>
                putOnboardingStatus(record, { onboarding_status: 'duplicate' }, 'Marked duplicate'),
            });
          },
        },
        {
          key: 'create',
          label: 'Create facility',
          icon: <PlusOutlined />,
          onClick: () => {
            setCreateSuggestion(record);
            const sf = record.suggested_facility || {};
            const matchedTier = tierOptions.find(
              (tier) => String(tier?.tier_name || '').toLowerCase() === String(sf.plan_type || '').toLowerCase()
            );
            createForm.setFieldsValue({
              name: sf.name || '',
              address: sf.address || '',
              city: sf.city || '',
              state: sf.state || '',
              facility_tier_id: matchedTier ? matchedTier.value : null,
            });
            setCreateModalOpen(true);
          },
        },
      ];
      return (
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={stop}
            aria-label="Row actions"
          />
        </Dropdown>
      );
    }

    return (
      <Space size={0} wrap onClick={stop}>
        <Button type="link" size="small" onClick={(e) => { stop(e); setDetail(record); }}>
          View
        </Button>
        {canManageOnboarding && (
          <>
            <Button type="link" size="small" icon={<LinkOutlined />} onClick={(e) => openLinkModal(record, e)}>
              Link
            </Button>
            {record.onboarding?.facility_id != null && (
              <Popconfirm
                title="Clear facility link?"
                description="Returns this suggestion to the pending queue."
                onConfirm={() => putOnboardingStatus(record, { facility_id: null }, 'Link cleared')}
              >
                <Button type="link" size="small" icon={<DisconnectOutlined />} onClick={stop}>
                  Unlink
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Mark as dismissed?"
              onConfirm={() =>
                putOnboardingStatus(record, { onboarding_status: 'dismissed' }, 'Marked dismissed')
              }
            >
              <Button type="link" size="small" danger icon={<StopOutlined />} onClick={stop}>
                Dismiss
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Mark as duplicate?"
              onConfirm={() =>
                putOnboardingStatus(record, { onboarding_status: 'duplicate' }, 'Marked duplicate')
              }
            >
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={stop}>
                Duplicate
              </Button>
            </Popconfirm>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                stop(e);
                setCreateSuggestion(record);
                const sf = record.suggested_facility || {};
                const matchedTier = tierOptions.find(
                  (tier) => String(tier?.tier_name || '').toLowerCase() === String(sf.plan_type || '').toLowerCase()
                );
                createForm.setFieldsValue({
                  name: sf.name || '',
                  address: sf.address || '',
                  city: sf.city || '',
                  state: sf.state || '',
                  facility_tier_id: matchedTier ? matchedTier.value : null,
                });
                setCreateModalOpen(true);
              }}
            >
              Create
            </Button>
          </>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Submitted',
      key: 'submitted_at',
      width: isMdUp ? 152 : 108,
      ellipsis: true,
      render: (_, record) =>
        isMdUp ? formatDateTime(record.submitted_at) : formatDateShort(record.submitted_at),
    },
    {
      title: 'Suggested facility',
      key: 'facility',
      ellipsis: true,
      render: (_, record) => {
        const sf = record.suggested_facility || {};
        const name = sf.name || '—';
        const loc = [sf.city, sf.state].filter(Boolean).join(', ') || '—';
        return (
          <div>
            <Text strong ellipsis={{ tooltip: name }}>
              {name}
            </Text>
            {isMdUp ? (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {loc}
                </Text>
              </div>
            ) : (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                  {loc}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Plan type',
      key: 'plan_type',
      width: 100,
      responsive: ['md'],
      ellipsis: true,
      render: (_, record) => record.suggested_facility?.plan_type || '—',
    },
    {
      title: 'Policy no.',
      key: 'policy_no',
      width: 118,
      responsive: ['md'],
      ellipsis: true,
      render: (_, record) => record.policy?.policy_no || '—',
    },
    {
      title: 'Enrollee',
      key: 'enrollee',
      width: 120,
      responsive: ['lg'],
      ellipsis: true,
      render: (_, record) => record.policy?.matched_enrollee?.name || '—',
    },
    {
      title: 'Organization',
      key: 'org',
      width: 140,
      responsive: ['lg'],
      ellipsis: true,
      render: (_, record) => orgCell(record),
    },
    {
      title: 'Onboarding',
      key: 'onboarding',
      width: 150,
      responsive: ['sm'],
      render: onboardingCol,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: canManageOnboarding ? (isMdUp ? 320 : 48) : 72,
      fixed: isMdUp ? 'right' : undefined,
      align: 'right',
      render: actionsCol,
    },
  ];

  const submitCreateFacility = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateSubmitting(true);
      const selectedTier = tierOptions.find((tier) => tier?.value === values.facility_tier_id);
      const resp = await lib.createFacilityV2({
        name: values.name,
        address: values.address,
        city: values.city,
        state: values.state,
        facility_tier_id: values.facility_tier_id ?? null,
        plan_type: selectedTier?.tier_name || 'all plans',
        suggestion_id: createSuggestion.id,
      });
      if (resp?.status === 'error') {
        notification.error({ message: resp.msg || 'Create failed', placement: 'topRight' });
        return;
      }
      if (Number(resp?.error) === 1) {
        notification.error({ message: resp.message || 'Create failed', placement: 'topRight' });
        return;
      }
      const body = resp?.data;
      if (body && Number(body.error) === 1) {
        notification.error({ message: body.message || 'Create failed', placement: 'topRight' });
        return;
      }
      const msg = body?.message || 'Facility created';
      const onboardStatus =
        body?.data?.onboarding?.onboarding_status
        || body?.data?.onboarding_status
        || body?.onboarding?.onboarding_status;
      const statusMeta = onboardStatus
        ? getFacilityOnboardingStatusMeta(onboardStatus)
        : null;
      if (body?.warning) {
        notification.warning({
          message: msg,
          description: [body.warning, statusMeta?.label].filter(Boolean).join(' · '),
          placement: 'topRight',
        });
      } else {
        notification.success({
          message: msg,
          description: statusMeta?.label,
          placement: 'topRight',
        });
      }
      setCreateModalOpen(false);
      setCreateSuggestion(null);
      createForm.resetFields();
      refreshAfterMutation();
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Create failed',
        placement: 'topRight',
      });
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (!user || !canAccessPage) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const emptyDescription = !hasActiveFilters
    ? 'No suggestions yet.'
    : 'No results — adjust filters or date range.';

  return (
    <div style={{ paddingTop: 5 }}>
      {!embedded ? (
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/facilities">Facilities</Link> },
            { title: 'Facility suggestions' },
          ]}
        />
      ) : null}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#1e293b' }}>
            Facility suggestions
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Enrollee-submitted facilities
            {canManageOnboarding ? ' · onboarding and linking' : ' · read-only'}
            {summaryLoading ? (
              <span> · Loading summary…</span>
            ) : (
              <span> · Showing {pagination.total} in selected range</span>
            )}
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
              Export CSV
            </Button>
            {!embedded ? (
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/facilities')}>
                Back to facilities
              </Button>
            ) : null}
          </Space>
        </Col>
      </Row>

      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          border: '1px solid #e2e8f0',
        }}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Input
              allowClear
              placeholder="Search…"
              prefix={<SearchOutlined style={{ color: '#64748b' }} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} sm={12} md={10} lg={7}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(v) => setDateRange(v)}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="State"
              style={{ width: '100%' }}
              value={stateFilter}
              onChange={setStateFilter}
              showSearch
              optionFilterProp="label"
              options={states.map((s) => ({ label: s.label, value: s.value }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Has organization"
              style={{ width: '100%' }}
              value={hasOrg}
              onChange={setHasOrg}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Policy in system"
              style={{ width: '100%' }}
              value={enrolleeMatched}
              onChange={setEnrolleeMatched}
              options={[
                { label: 'Matched', value: '1' },
                { label: 'Unmatched', value: '0' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Onboarded"
              style={{ width: '100%' }}
              value={onboardedFilter}
              onChange={setOnboardedFilter}
              options={[
                { label: 'Yes (linked to facility)', value: '1' },
                { label: 'Not yet', value: '0' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Onboarding status"
              style={{ width: '100%' }}
              value={onboardingStatusFilter}
              onChange={setOnboardingStatusFilter}
              options={ONBOARDING_STATUS_OPTIONS}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => load(pagination.current, pagination.pageSize, false)}
              loading={loading && !searchLoading}
            >
              Refresh
            </Button>
          </Col>
          {searchLoading && (
            <Col>
              <Text type="secondary">Searching…</Text>
            </Col>
          )}
        </Row>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic title="Total" value={summary.total} loading={summaryLoading} />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Matched enrollee"
              value={summary.with_matched_enrollee}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Unmatched policy"
              value={summary.unmatched_enrollee}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="With organization"
              value={summary.with_organization}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Without organization"
              value={summary.without_organization}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Onboarded (facility)"
              value={summary.with_onboarded_facility}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Pending queue"
              value={summary.onboarding_pending}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Dismissed"
              value={summary.onboarding_dismissed}
              loading={summaryLoading}
            />
          </Col>
          <Col xs={12} sm={8} md={4} lg={3}>
            <Statistic
              title="Duplicate"
              value={summary.onboarding_duplicate}
              loading={summaryLoading}
            />
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message="Error loading suggestions"
          description={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
        bodyStyle={screens.xs ? { padding: '12px 8px' } : undefined}
      >
        {loading && rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Loading suggestions…</Text>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <Empty description={emptyDescription} style={{ padding: '40px 20px' }} />
        ) : (
          <>
            <div className="facility-suggestions-table-wrap">
              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                size={isMdUp ? 'middle' : 'small'}
                tableLayout="fixed"
                scroll={{ x: isMdUp ? 1100 : 'max-content' }}
                onRow={(record) => ({
                  onClick: () => setDetail(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </div>
            {pagination.total > 0 && (
              <div
                style={{
                  marginTop: 16,
                  textAlign: screens.xs ? 'center' : 'right',
                  overflowX: 'auto',
                }}
              >
                <Pagination
                  simple={Boolean(screens.xs)}
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger={!screens.xs}
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={
                    screens.xs
                      ? undefined
                      : (total, range) => `${range[0]}–${range[1]} of ${total}`
                  }
                  onChange={(page, pageSize) => {
                    const size = pageSize || pagination.pageSize;
                    load(page, size, false);
                  }}
                />
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        title={`Link suggestion #${linkSuggestion?.id ?? ''} to facility`}
        open={linkModalOpen}
        onCancel={() => {
          setLinkModalOpen(false);
          setLinkSuggestion(null);
        }}
        onOk={submitLink}
        confirmLoading={linkSubmitting}
        okText="Link"
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Search by facility name or code, then select a row. This sets onboarding to linked.
        </Text>
        <Select
          showSearch
          allowClear
          placeholder="Type at least 2 characters to search"
          style={{ width: '100%' }}
          filterOption={false}
          onSearch={searchFacilities}
          notFoundContent={facilitySearchLoading ? <Spin size="small" /> : null}
          options={facilityOptions}
          value={linkFacilityId}
          onChange={setLinkFacilityId}
          loading={facilitySearchLoading}
        />
      </Modal>

      <Modal
        title={`Create facility from suggestion #${createSuggestion?.id ?? ''}`}
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateSuggestion(null);
          createForm.resetFields();
        }}
        onOk={submitCreateFacility}
        confirmLoading={createSubmitting}
        okText="Create facility"
        width={480}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item name="state" label="State">
            <Input />
          </Form.Item>
          <Form.Item name="facility_tier_id" label="Minimum Tier">
            <Select
              allowClear
              options={tierOptions}
              loading={loadingTiers}
              placeholder="Select minimum tier"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`Suggestion #${detail?.id ?? ''}`}
        width={520}
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        destroyOnClose
        extra={
          canManageOnboarding && detail ? (
            <Space wrap>
              <Button size="small" type="primary" icon={<LinkOutlined />} onClick={(e) => openLinkModal(detail, e)}>
                Link to facility
              </Button>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setCreateSuggestion(detail);
                  const sf = detail.suggested_facility || {};
                  const matchedTier = tierOptions.find(
                    (tier) => String(tier?.tier_name || '').toLowerCase() === String(sf.plan_type || '').toLowerCase()
                  );
                  createForm.setFieldsValue({
                    name: sf.name || '',
                    address: sf.address || '',
                    city: sf.city || '',
                    state: sf.state || '',
                    facility_tier_id: matchedTier ? matchedTier.value : null,
                  });
                  setCreateModalOpen(true);
                }}
              >
                Create facility
              </Button>
            </Space>
          ) : null
        }
      >
        {detail && (
          <>
            <Title level={5}>Onboarding</Title>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Status">
                {statusTag(detail.onboarding?.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Facility">
                {detail.onboarding?.facility_name || '—'}
                {detail.onboarding?.facility_id != null ? (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    (ID {detail.onboarding.facility_id})
                  </Text>
                ) : null}
              </Descriptions.Item>
              <Descriptions.Item label="Onboarded at">
                {formatDateTime(detail.onboarding?.onboarded_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Onboarded by (user id)">
                {detail.onboarding?.onboarded_by ?? '—'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>Suggested facility</Title>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Name">
                {detail.suggested_facility?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {detail.suggested_facility?.address || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="City / State">
                {[detail.suggested_facility?.city, detail.suggested_facility?.state]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Plan type">
                {detail.suggested_facility?.plan_type || '—'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>Policy</Title>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Policy no.">
                {detail.policy?.policy_no || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Policy name at submission">
                {detail.policy?.policy_name_at_submission || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted at">
                {formatDateTime(detail.submitted_at)}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>Matched enrollee</Title>
            {detail.policy?.matched_enrollee ? (
              <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
                <Descriptions.Item label="ID">{detail.policy.matched_enrollee.id}</Descriptions.Item>
                <Descriptions.Item label="Name">
                  {detail.policy.matched_enrollee.name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {detail.policy.matched_enrollee.email || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {detail.policy.matched_enrollee.phone || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Insurance package">
                  {detail.policy.matched_enrollee.insurance_package || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {detail.policy.matched_enrollee.category || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Organization code">
                  {detail.policy.matched_enrollee.social_no || '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                No enrollee row matches this policy number (typo, removed enrollee, or legacy
                data). Policy details above are still from the submission.
              </Text>
            )}

            <Title level={5}>Organization</Title>
            {detail.enrollee_belongs_to_organization && detail.organization ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Name">{detail.organization.org_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Org ID">{detail.organization.org_id}</Descriptions.Item>
                <Descriptions.Item label="Org code">
                  {detail.organization.org_social_no || '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Tag>No organization linked for this enrollee</Tag>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

export default FacilitySuggestions;
